/**
 * Main application layout with header and content area.
 *
 * Features:
 * - Header with logo, navigation, and user menu
 * - Centered mobile header with logo + site name (no drawer)
 * - User avatar dropdown with logout option
 * - Outlet for nested route content
 *
 * Called by:
 * - App.tsx as wrapper for protected routes
 */
import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Layout, Dropdown, Avatar, Button, Typography } from 'antd';
import { UserOutlined, LogoutOutlined, BookOutlined, HomeOutlined, SettingOutlined, BarChartOutlined, FormOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import ReportIssueButton from './ReportIssueButton';

const { Header, Content } = Layout;
const { Text } = Typography;

const MOBILE_BREAKPOINT = 768;

/**
 * Application layout component.
 * Provides consistent header and navigation across all pages.
 */
function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const userMenuItems = [
    {
      key: 'info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <Text strong>{user?.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user?.email}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px', textTransform: 'capitalize' }}>
            {user?.role}
            {!user?.approved && ' (Pending)'}
          </Text>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      onClick: handleLogout,
    },
  ];

  const navLinks = [
    { key: 'home', icon: <HomeOutlined />, label: 'Home', path: '/reviewer' },
    { key: 'essays', icon: <BookOutlined />, label: 'Essays', path: '/reviewer/essays' },
    ...(user?.role === 'author' ? [
      { key: 'notes', icon: <FormOutlined />, label: 'Notes', path: '/reviewer/notes' },
      { key: 'admin', icon: <SettingOutlined />, label: 'Admin', path: '/reviewer/admin' },
      { key: 'analytics', icon: <BarChartOutlined />, label: 'Analytics', path: '/reviewer/analytics' },
    ] : []),
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Header with navigation and user menu */}
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          padding: isMobile ? '0 12px' : '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderBottom: '1px solid #333',
          height: 48,
          lineHeight: '48px',
        }}
      >
        {/* Mobile: invisible spacer for centering balance */}
        {isMobile && (
          <div style={{ width: 36, flexShrink: 0 }} />
        )}

        {/* Logo and navigation */}
        {isMobile ? (
          /* Mobile: centered logo + site name */
          <Link
            to="/reviewer"
            style={{
              color: '#f5f5f5',
              fontSize: '1.1rem',
              fontWeight: 600,
              fontFamily: 'var(--font-serif)',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <img src="/logo-wide.png" alt="Compendium" style={{ height: 26 }} />
          </Link>
        ) : (
          /* Desktop: logo + inline nav links */
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <Link
              to="/reviewer"
              style={{
                color: '#f5f5f5',
                fontSize: '1.15rem',
                fontWeight: 600,
                fontFamily: 'var(--font-serif)',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              <img src="/logo-wide.png" alt="Compendium" style={{ height: 28 }} />
            </Link>

            <nav style={{ display: 'flex', gap: 4 }}>
              {navLinks.map((item) => (
                <Link
                  key={item.key}
                  to={item.path}
                  style={{
                    color: '#a0a0a0',
                    fontSize: '0.85rem',
                    padding: '4px 10px',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#a0a0a0')}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}

        {/* Right side: bug report + user menu */}
        <ReportIssueButton variant="header" />
        <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: 'auto',
              padding: '4px 8px',
              flexShrink: 0,
            }}
          >
            <Avatar
              src={user?.avatarUrl}
              icon={!user?.avatarUrl && <UserOutlined />}
              size="small"
            />
            {!isMobile && <Text style={{ color: '#f5f5f5' }}>{user?.name}</Text>}
          </Button>
        </Dropdown>
      </Header>

      {/* Main content area */}
      <Content
        style={{
          padding: isMobile ? '16px 12px' : '24px',
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
        }}
      >
        <Outlet />
      </Content>
    </Layout>
  );
}

export default AppLayout;
